// pages/portal/SettingsPage.js —— 门户「设置中心」：组织成员 / 功能角色 / 个性配置
// 依据《云平台账号权限》手册，界面为轮廓级演示（复用 stationData 演示数据契约，不做后端对接）。
// 说明：组织成员 / 功能角色的增删改均为前端本地行为（演示），用于呈现实交互；后端接口待接入。
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Input, Select, Tree, Steps, message } from 'antd';
import {
  ArrowLeftOutlined, ApartmentOutlined, TeamOutlined, SafetyCertificateOutlined,
  BgColorsOutlined, PlusOutlined, EditOutlined, CopyOutlined, DeleteOutlined,
  KeyOutlined, SearchOutlined, UploadOutlined, GlobalOutlined, PictureOutlined, ShopOutlined,
} from '@ant-design/icons';
import { IA, pick, getLeaves } from '../../config/menuConfig';
import { api } from '../../services/api';
import {
  H_DEPT_TREE, H_MEMBERS, H_ASSIGN_MODES, PORTAL_ROLES, PORTAL_INVITES,
  PORTAL_PLATFORM_LANGS, H_THEME_ITEMS, PORTAL_PRODUCTS, PORTAL_REGIONS,
  STATIONS, STATION_TAGS, H_COMPANIES,
} from '../../config/stationData';

const TABS = [
  { key: 'member', label: '组织成员', icon: <TeamOutlined /> },
  { key: 'role', label: '功能角色', icon: <SafetyCertificateOutlined /> },
  { key: 'theme', label: '个性配置', icon: <BgColorsOutlined /> },
];

// 权限配置 —— 菜单树由云平台信息架构 IA 派生（前台/后台 → 分区 → 菜单页面）
const MENU_TREE = IA.map((g) => ({
  title: pick(g.label, 'zh'),
  key: g.key,
  children: (g.children || []).map((s) => ({
    title: pick(s.label, 'zh'),
    key: s.key,
    children: (s.children || []).map((l) => ({ title: pick(l.label, 'zh'), key: l.key })),
  })),
}));
const ALL_MENUS = getLeaves().map((l) => l.key);
const LEAF_KEYS = new Set(ALL_MENUS);
const LABEL_BY_KEY = getLeaves().reduce((acc, l) => ({ ...acc, [l.key]: pick(l.label, 'zh') }), {});

// 内置角色默认可访问项（自定义角色默认为空，需在「权限配置」中自行勾选）
// 同时供门户页判断「该角色在某产品下是否还有可见菜单」
export const PRESET_PERMS = {
  管理员: ALL_MENUS,
  储能车: ['/app/display/single', '/app/monitor/station'],
  现场运维: ['/app/monitor/station', '/app/om/workorder', '/app/om/plan'],
  运营专员: ['/app/display/multi', '/app/display/single', '/app/analysis/battery', '/app/analysis/report', '/app/biz/analysis'],
  只读访客: ['/app/display/multi', '/app/display/single', '/app/display/map', '/app/analysis/report'],
};

// 可指派的部门 / 角色由下方组件内状态动态派生（内置「所有成员」不可指派）

const SettingsPage = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('member');
  // 平台不开放自主注册：注册/邀请链接只能由超级管理员（isadmin=2）生成
  const currentUser = (() => {
    try { return JSON.parse(localStorage.getItem('currentUser') || '{}'); } catch (e) { return {}; }
  })();
  const isSuperAdmin = currentUser.isadmin === 2;
  const [activeDept, setActiveDept] = useState('d-all');
  // 添加成员向导：0 基本信息 → 1 站点分配
  const [mStep, setMStep] = useState(0);
  const [assign, setAssign] = useState({ mode: H_ASSIGN_MODES[0], regions: [], tags: [], company: undefined, stations: [] });
  // 平台名称：优先读取已保存值，使个性配置与登录页 / 门户保持同一来源
  const [langs, setLangs] = useState(() => PORTAL_PLATFORM_LANGS.map((l) => {
    const saved = l.key === 'zh' ? localStorage.getItem('platformNameZh')
      : l.key === 'en' ? localStorage.getItem('platformNameEn') : null;
    return saved ? { ...l, value: saved } : { ...l };
  }));

  // 组织成员（本地演示状态）
  const [depts, setDepts] = useState(H_DEPT_TREE.map((d) => ({ ...d })));
  const [members, setMembers] = useState(H_MEMBERS.map((m) => ({ ...m })));
  const [invites, setInvites] = useState(PORTAL_INVITES.map((v) => ({ ...v })));
  // 功能角色（本地演示状态）
  const [roles, setRoles] = useState(PORTAL_ROLES.map((r) => ({ ...r })));

  // 弹窗
  const [deptModal, setDeptModal] = useState({ open: false, key: null, name: '' });
  const [memberModal, setMemberModal] = useState(false);
  const [memberForm, setMemberForm] = useState({ account: '', name: '', phone: '', dept: '', role: '' });
  const [roleModal, setRoleModal] = useState({ open: false, name: '', copyFrom: null });
  const [editMember, setEditMember] = useState({ open: false, key: null, name: '', dept: '', role: '' });
  // 平台名称（个性配置保存后即时反映到设置中心右上角）
  const [platformName, setPlatformName] = useState(() => localStorage.getItem('platformNameZh') || '智慧能源管理云平台');

  // 功能角色 · 权限配置（二级视图）+ 权限详情
  const [permsMap, setPermsMap] = useState({});
  const [permView, setPermView] = useState(null); // { key, name }
  const [permProducts, setPermProducts] = useState([]);
  const [permMenus, setPermMenus] = useState([]);
  const [detailPerm, setDetailPerm] = useState({ open: false, role: '', products: [], menus: [] });

  // ---------- 真实后端：部门 / 角色 / 待激活邀请（缺陷161：邀请链接走真实接口） ----------
  const [org, setOrg] = useState({ depts: [], roles: [] });
  const [realMode, setRealMode] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!localStorage.getItem('sessionToken')) return;
      try {
        const [dRes, rRes, iRes] = await Promise.all([
          api.getDepartmentList(), api.getRoleList(), api.getInvitationList(0),
        ]);
        if (!alive || !dRes.success || !rRes.success) return;
        setOrg({ depts: dRes.data || [], roles: rRes.data || [] });
        setRealMode(true);
        if (iRes.success && Array.isArray(iRes.data)) {
          // 已激活/已取消的邀请不在列表中展示，仅展示待激活
          setInvites(iRes.data.map((v) => ({
            key: `i-${v.id}`,
            realId: v.id,
            code: v.code,
            invitee: '待加入',
            dept: v.department_name || '—',
            role: v.role_name || '—',
            created: String(v.created_at).slice(0, 10),
            expire: String(v.expires_at).slice(0, 10),
            state: '待激活',
            scope: `邀请码：${v.code}`,
            link: `${window.location.origin}/referral/${v.code}`,
          })));
        }
      } catch (e) { /* 非公司管理员或接口不可用：回退到演示数据 */ }
    })();
    return () => { alive = false; };
  }, []);

  // 可指派的部门 / 角色（随新增部门、新增角色实时更新；已接入后端时使用公司真实部门与角色）
  const assignDepts = depts.filter((d) => !d.builtin).map((d) => d.title);
  const assignRoles = roles.map((r) => r.name);
  const deptOptions = realMode ? org.depts.map((d) => d.name) : assignDepts;
  const roleOptions = realMode ? org.roles.map((r) => r.name) : assignRoles;
  const canEdit = (r) => !r.builtin || (!r.service);
  const canDelete = (r) => !r.builtin;
  const activeDeptTitle = (depts.find((d) => d.key === activeDept) || {}).title;
  // 依据手册 p10：角色为管理员的成员展示在成员列表顶部
  const byAdminFirst = (a, b) => (b.role === '管理员' ? 1 : 0) - (a.role === '管理员' ? 1 : 0);
  const visibleMembers = (activeDept === 'd-all'
    ? members
    : members.filter((m) => m.dept === activeDeptTitle)).slice().sort(byAdminFirst);
  // 角色权限（本地态优先，未配置时回退到内置预设）
  const permsOf = (r) => permsMap[r.key] || { products: ['能源管家'], menus: PRESET_PERMS[r.name] || [] };

  // ---------- 部门 ----------
  const openAddDept = () => setDeptModal({ open: true, key: null, name: '' });
  const openRenameDept = (d) => setDeptModal({ open: true, key: d.key, name: d.title });
  const submitDept = () => {
    const name = deptModal.name.trim();
    if (!name) { message.warning('请输入部门名称'); return; }
    if (depts.some((d) => d.title === name && d.key !== deptModal.key)) { message.warning('部门名称已存在'); return; }
    if (deptModal.key) {
      const oldTitle = (depts.find((d) => d.key === deptModal.key) || {}).title;
      setDepts((ds) => ds.map((d) => (d.key === deptModal.key ? { ...d, title: name } : d)));
      setMembers((ms) => ms.map((m) => (m.dept === oldTitle ? { ...m, dept: name } : m)));
      message.success('部门名称已修改');
    } else {
      setDepts((ds) => [...ds, { key: `d-${Date.now()}`, title: name }]);
      message.success('部门已新建');
    }
    setDeptModal({ open: false, key: null, name: '' });
  };
  const removeDept = (d) => {
    if (d.builtin) { message.warning('内置部门不可删除'); return; }
    if (members.some((m) => m.dept === d.title)) { message.warning('该部门下存在账号，不可删除'); return; }
    setDepts((ds) => ds.filter((x) => x.key !== d.key));
    if (activeDept === d.key) setActiveDept('d-all');
    message.success('部门已删除');
  };

  // ---------- 成员 ----------
  const openAddMember = () => {
    setMemberForm({ account: '', name: '', phone: '', dept: deptOptions[0], role: roleOptions[0] });
    setAssign({ mode: H_ASSIGN_MODES[0], regions: [], tags: [], company: undefined, stations: [] });
    setMStep(0);
    setMemberModal(true);
  };
  // 站点分配结果文案（写入邀请记录，作为成员在管家内的数据权限）
  const assignScope = () => {
    const { mode, regions, tags, company, stations } = assign;
    if (mode === '省市区') return regions.length ? `地区：${regions.join('、')}` : '地区：未指定';
    if (mode === '站点标签') return tags.length ? `标签：${tags.join('、')}` : '标签：未指定';
    if (mode === '外部公司') return company ? `外部公司：${company}` : '外部公司：未指定';
    return stations.length ? `指定站点：${stations.length} 个` : '指定站点：未指定';
  };
  const goMemberNext = () => {
    const { account, name } = memberForm;
    if (!account.trim() || !name.trim()) { message.warning('请填写账号名与姓名'); return; }
    if (members.some((m) => m.account === account.trim())) { message.warning('该账号已存在'); return; }
    setMStep(1);
  };
  const submitMember = async () => {
    const { account, name, phone, dept, role } = memberForm;
    if (!account.trim() || !name.trim()) { message.warning('请填写账号名与姓名'); setMStep(0); return; }
    if (members.some((m) => m.account === account.trim())) { message.warning('该账号已存在'); setMStep(0); return; }

    // 缺陷161：已接入后端时生成真实邀请链接（15 天有效、一次性），被邀请人凭链接注册后才会成为公司成员
    if (realMode) {
      const deptObj = org.depts.find((d) => d.name === dept);
      const roleObj = org.roles.find((r) => r.name === role);
      try {
        const res = await api.generateInvite({
          department_id: deptObj ? deptObj.id : null,
          role_id: roleObj ? roleObj.id : null,
          station_ids: [],
        });
        if (!res.success) { message.error(res.message || '生成邀请链接失败'); return; }
        const link = `${window.location.origin}/referral/${res.data.code}`;
        setInvites((vs) => [{
          key: `i-${res.data.id}`,
          realId: res.data.id,
          code: res.data.code,
          invitee: `${name.trim()}（${account.trim()}）`,
          dept: dept || '—',
          role: role || '—',
          created: new Date().toISOString().slice(0, 10),
          expire: String(res.data.expires_at).slice(0, 10),
          state: '待激活',
          scope: `邀请码：${res.data.code}`,
          link,
        }, ...vs]);
        setMemberModal(false);
        setMStep(0);
        message.success(`邀请链接已生成，请发送给被邀请人：${link}`);
      } catch (e) {
        message.error(e.message || '生成邀请链接失败');
      }
      return;
    }

    const key = `m-${Date.now()}`;
    const link = `https://ems.demo/invite/${key}`;
    setMembers((ms) => [...ms, { key, account: account.trim(), name: name.trim(), role, phone: phone.trim() || '—', dept, state: '启用' }]);
    // 依据手册 p9：添加成员时生成邀请链接，进入「待激活的邀请」
    setInvites((vs) => [...vs, {
      key, invitee: name.trim(), dept, role, created: new Date().toISOString().slice(0, 10), expire: '15 天后', state: '待激活', scope: assignScope(), link,
    }]);
    setMemberModal(false);
    setMStep(0);
    message.success(`已添加成员并生成邀请链接：${link}`);
  };
  // 依据手册 p11：唯一的启用管理员不可被禁用 / 移出 / 改角色
  const isLastAdmin = (m) => m.state === '启用' && m.role === '管理员'
    && members.filter((x) => x.state === '启用' && x.role === '管理员').length <= 1;
  const toggleMember = (m) => {
    if (m.state === '启用' && isLastAdmin(m)) { message.error('操作失败：需要保留至少一个启用的管理员账号'); return; }
    setMembers((ms) => ms.map((x) => (x.key === m.key ? { ...x, state: x.state === '启用' ? '禁用' : '启用' } : x)));
    message.success(`成员「${m.name}」已${m.state === '启用' ? '禁用' : '启用'}`);
  };
  const openEditMember = (m) => setEditMember({ open: true, key: m.key, name: m.name, dept: m.dept, role: m.role });
  const submitEditMember = () => {
    const m = members.find((x) => x.key === editMember.key);
    if (m && editMember.role !== '管理员' && isLastAdmin(m)) { message.error('操作失败：需要保留至少一个启用的管理员账号'); return; }
    setMembers((ms) => ms.map((x) => (x.key === editMember.key ? { ...x, dept: editMember.dept, role: editMember.role } : x)));
    setEditMember((s) => ({ ...s, open: false }));
    message.success('已更新成员的部门与角色');
  };
  // 依据手册 p11：移出公司后，成员与本公司关联解除，再次加入需重新邀请
  const removeMember = (m) => {
    if (isLastAdmin(m)) { message.error('操作失败：需要保留至少一个启用的管理员账号'); return; }
    setMembers((ms) => ms.filter((x) => x.key !== m.key));
    message.success(`已将「${m.name}」移出公司`);
  };
  const revokeInvite = async (v) => {
    // 缺陷161：真实邀请调用后端取消接口，取消后链接立即失效
    if (v.realId) {
      try {
        const res = await api.cancelInvite(v.realId);
        if (!res.success) { message.error(res.message || '取消邀请失败'); return; }
      } catch (e) { message.error(e.message || '取消邀请失败'); return; }
    }
    setInvites((vs) => vs.filter((x) => x.key !== v.key));
    message.success('邀请已取消');
  };
  // 依据手册 p9：已建立的邀请链接可再次复制，或取消邀请（取消后链接失效）
  const copyInvite = (v) => {
    const link = v.link || `https://ems.demo/invite/${v.key}`;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(link).then(
        () => message.success('邀请链接已复制，可发送给被邀请人'),
        () => message.success(`邀请链接：${link}`),
      );
    } else {
      message.success(`邀请链接：${link}`);
    }
  };
  // 依据手册 p14-p16：平台名称保存后需影响设置中心 / 登录页 / 门户
  const saveTheme = () => {
    const zh = ((langs.find((l) => l.key === 'zh') || {}).value || '').trim();
    const en = ((langs.find((l) => l.key === 'en') || {}).value || '').trim();
    if (!zh || !en) { message.warning('中文、英文平台名称为必填项'); return; }
    localStorage.setItem('platformNameZh', zh);
    localStorage.setItem('platformNameEn', en);
    localStorage.setItem('platformName', zh);
    setPlatformName(zh);
    message.success('平台名称已保存，将在设置中心 / 登录页 / 门户生效');
  };

  // ---------- 角色 ----------
  // 依据手册 p14：删除角色前需校验该角色是否被账号使用
  const roleUsed = (r) => r.members > 0 || members.some((m) => m.role === r.name);
  const submitRole = () => {
    const name = roleModal.name.trim();
    if (!name) { message.warning('请输入角色名称'); return; }
    if (roles.some((r) => r.name === name)) { message.warning('角色名称不可与已有角色重复'); return; }
    const src = roleModal.copyFrom ? roles.find((r) => r.key === roleModal.copyFrom) : null;
    const key = `r-${Date.now()}`;
    setRoles((rs) => [...rs, {
      key, name, members: 0, builtin: false, service: false,
      desc: src ? `复制自「${src.name}」` : '自定义角色，需配置菜单权限',
    }]);
    // 复制角色：连同原角色的权限配置一起复制（依据手册 p13）
    if (src) setPermsMap((m) => ({ ...m, [key]: permsOf(src) }));
    setRoleModal({ open: false, name: '', copyFrom: null });
    message.success(src ? '复制成功' : '角色已新增，请点击「权限配置」配置菜单权限');
  };
  // 依据手册 p13：复制角色需填写新的角色名称（不可与已有角色名称重复）
  const copyRole = (r) => setRoleModal({ open: true, name: `${r.name} 副本`, copyFrom: r.key });
  const removeRole = (r) => {
    if (!canDelete(r)) { message.warning('内置角色不可删除'); return; }
    if (roleUsed(r)) { message.error('该角色正在被用户使用，无法删除'); return; }
    setRoles((rs) => rs.filter((x) => x.key !== r.key));
    message.success(`已删除角色「${r.name}」`);
  };

  // ---------- 角色 · 权限配置（二级视图） ----------
  const openPerm = (r) => {
    const saved = permsOf(r);
    setPermView({ key: r.key, name: r.name });
    setPermProducts(saved.products);
    setPermMenus(saved.menus);
  };
  const toggleProduct = (name) => setPermProducts((ps) => (ps.includes(name) ? ps.filter((x) => x !== name) : [...ps, name]));
  const savePerms = () => {
    if (!permProducts.length) { message.warning('请至少勾选一个产品'); return; }
    setPermsMap((m) => ({ ...m, [permView.key]: { products: permProducts, menus: permMenus } }));
    message.success(`已保存「${permView.name}」权限配置（产品 ${permProducts.length} 个 / 菜单 ${permMenus.length} 项）`);
    setPermView(null);
  };
  const openDetail = (r) => {
    const saved = permsOf(r);
    setDetailPerm({ open: true, role: r.name, products: saved.products, menus: saved.menus });
  };

  return (
    <div className="sp-page">
      <div className="sp-header">
        <div className="sp-header-left">
          <button type="button" className="sp-back" onClick={() => navigate('/portal')}>
            <ArrowLeftOutlined /> 返回门户
          </button>
          <span className="sp-title">设置中心</span>
          <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>{platformName}</span>
        </div>
        <img src="/logo.png" alt="logo" />
      </div>

      <div className="sp-body">
        {permView ? (
          /* ---------------- 权限配置 · 二级页面 ---------------- */
          <main className="sp-main wide">
            <div className="sp-toolbar">
              <button type="button" className="sp-btn ghost" onClick={() => setPermView(null)}>
                <ArrowLeftOutlined /> 返回功能角色
              </button>
              <span className="sp-toolbar-tip">为角色「<b>{permView.name}</b>」配置可访问的产品与功能菜单</span>
            </div>

            <div className="sp-card">
              <div className="sp-card-title"><ShopOutlined /> 勾选产品<small>该角色可使用的产品（可多选）</small></div>
              <div className="sp-options">
                {PORTAL_PRODUCTS.map((p) => {
                  const on = permProducts.includes(p.name);
                  return (
                    <label key={p.key} className={`sp-option${on ? ' active' : ''}`}>
                      <input type="checkbox" checked={on} onChange={() => toggleProduct(p.name)} />
                      <span>
                        {p.name}
                        <em style={{ display: 'block', fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>{p.desc}</em>
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="sp-card">
              <div className="sp-card-title">
                功能菜单权限
                <small>勾选该角色在产品中可见的菜单页面（已选 {permMenus.length} / {ALL_MENUS.length}）</small>
              </div>
              <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
                <button type="button" className="sp-btn ghost" onClick={() => setPermMenus(ALL_MENUS)}>全选</button>
                <button type="button" className="sp-btn ghost" onClick={() => setPermMenus([])}>清空</button>
              </div>
              <Tree
                checkable
                selectable={false}
                defaultExpandAll
                treeData={MENU_TREE}
                checkedKeys={permMenus}
                onCheck={(keys) => setPermMenus((Array.isArray(keys) ? keys : keys.checked).filter((k) => LEAF_KEYS.has(k)))}
              />
              <div style={{ marginTop: 12, fontSize: 12, color: 'rgba(255,255,255,0.45)', lineHeight: 1.9 }}>
                此处仅管理菜单权限，即控制成员能看到哪些产品的哪些菜单页面；成员可见的站点在能源管家内另行管理。
              </div>
            </div>

            <div className="sp-actions">
              <button type="button" className="sp-btn primary" onClick={savePerms}>保存</button>
            </div>
          </main>
        ) : (
        <>
        <div className="sp-tabs">
          {TABS.map((t) => (
            <button
              type="button"
              key={t.key}
              className={`sp-tab${tab === t.key ? ' active' : ''}`}
              onClick={() => setTab(t.key)}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {/* ---------------- 组织成员 ---------------- */}
        {tab === 'member' ? (
          <div className="sp-split">
            <aside className="sp-tree">
              <div className="sp-tree-head"><ApartmentOutlined /> 部门</div>
              {depts.map((d) => (
                <div
                  key={d.key}
                  className={`sp-tree-node${activeDept === d.key ? ' active' : ''}`}
                  onClick={() => setActiveDept(d.key)}
                >
                  <span className="sp-tree-title">{d.title}</span>
                  {d.builtin ? <span className="sp-tag mini">内置</span> : null}
                  {!d.builtin ? (
                    <span className="sp-tree-ops" onClick={(e) => e.stopPropagation()}>
                      <button type="button" title="重命名" onClick={() => openRenameDept(d)}><EditOutlined /></button>
                      <button type="button" title="删除" className="danger" onClick={() => removeDept(d)}><DeleteOutlined /></button>
                    </span>
                  ) : null}
                </div>
              ))}
              <button type="button" className="sp-btn ghost block" onClick={openAddDept}>
                <PlusOutlined /> 新增部门
              </button>
            </aside>

            <main className="sp-main">
              <div className="sp-toolbar">
                {isSuperAdmin ? (
                  <button type="button" className="sp-btn primary" onClick={openAddMember}>
                    <PlusOutlined /> 添加成员
                  </button>
                ) : (
                  <span className="sp-toolbar-note">注册/邀请链接仅超级管理员可生成</span>
                )}
                <div className="sp-search">
                  <SearchOutlined />
                  <input placeholder="输入部门名称搜索" />
                </div>
              </div>

              <div className="sp-card">
                <div className="sp-card-title">账号列表<small>共 {visibleMembers.length} 个成员</small></div>
                <table className="sp-table">
                  <thead>
                    <tr>
                      <th>账号名</th><th>姓名</th><th>角色</th><th>手机号</th><th>部门</th><th>状态</th><th>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleMembers.map((m) => (
                      <tr key={m.key}>
                        <td>{m.account}</td>
                        <td>{m.name}</td>
                        <td>{m.role}</td>
                        <td>{m.phone}</td>
                        <td>{m.dept}</td>
                        <td><span className={`sp-state${m.state === '启用' ? ' on' : ' off'}`}>{m.state}</span></td>
                        <td className="sp-ops">
                          <button type="button" onClick={() => openEditMember(m)}>编辑</button>
                          <button type="button" className="danger" onClick={() => toggleMember(m)}>
                            {m.state === '启用' ? '禁用' : '启用'}
                          </button>
                          <button type="button" className="danger" onClick={() => removeMember(m)}>移出公司</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="sp-card">
                <div className="sp-card-title">待激活的邀请<small>邀请链接有效期默认 15 天</small></div>
                <table className="sp-table">
                  <thead>
                    <tr><th>被邀请人</th><th>部门</th><th>角色</th><th>邀请时间</th><th>过期时间</th><th>状态</th><th>操作</th></tr>
                  </thead>
                  <tbody>
                    {invites.map((v) => (
                      <tr key={v.key}>
                        <td>
                          {v.invitee}
                          {v.scope ? <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.5)', marginTop: 3 }}>{v.scope}</div> : null}
                        </td>
                        <td>{v.dept}</td>
                        <td>{v.role}</td>
                        <td>{v.created}</td>
                        <td>{v.expire}</td>
                        <td><span className={`sp-state${v.state === '待激活' ? ' on' : ' warn'}`}>{v.state}</span></td>
                        <td className="sp-ops">
                          <button type="button" onClick={() => copyInvite(v)}>复制链接</button>
                          <button type="button" className="danger" onClick={() => revokeInvite(v)}>取消邀请</button>
                        </td>
                      </tr>
                    ))}
                    {invites.length === 0 ? (
                      <tr><td colSpan={7} style={{ textAlign: 'center', color: 'rgba(255,255,255,0.4)' }}>暂无待激活的邀请</td></tr>
                    ) : null}
                  </tbody>
                </table>
              </div>

              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', lineHeight: 1.9 }}>
                点击「添加成员」，依次填写基本信息并为成员分配可见站点（省市区 / 站点标签 / 外部公司 / 手动选择）。
              </div>
            </main>
          </div>
        ) : null}

        {/* ---------------- 功能角色 ---------------- */}
        {tab === 'role' ? (
          <main className="sp-main wide">
            <div className="sp-toolbar">
              <button type="button" className="sp-btn primary" onClick={() => setRoleModal({ open: true, name: '' })}>
                <PlusOutlined /> 新增角色
              </button>
              <span className="sp-toolbar-tip">带 <b>*</b> 为系统内置角色，不可删除</span>
            </div>

            <div className="sp-card">
              <div className="sp-card-title">角色列表<small>共 {roles.length} 个角色</small></div>
              <table className="sp-table">
                <thead>
                  <tr><th>角色名称</th><th>成员数</th><th>角色来源</th><th>说明</th><th>操作</th></tr>
                </thead>
                <tbody>
                  {roles.map((r) => (
                    <tr key={r.key}>
                      <td className="sp-role-name">
                        {r.name}
                        {r.builtin ? <span className="sp-tag mini">*</span> : null}
                        {r.service ? <span className="sp-tag mini service">服务角色</span> : null}
                      </td>
                      <td>{r.members} 人</td>
                      <td>{r.builtin ? '系统内置' : '自定义'}</td>
                      <td className="sp-desc">{r.desc}</td>
                      <td className="sp-ops">
                        <button
                          type="button"
                          className={canEdit(r) ? '' : 'disabled'}
                          disabled={!canEdit(r)}
                          onClick={() => openPerm(r)}
                        >
                          <EditOutlined /> 权限配置
                        </button>
                        <button
                          type="button"
                          className={r.service ? 'disabled' : ''}
                          disabled={r.service}
                          onClick={() => copyRole(r)}
                        >
                          <CopyOutlined /> 复制
                        </button>
                        <button
                          type="button"
                          className={canDelete(r) ? 'danger' : 'disabled'}
                          disabled={!canDelete(r)}
                          onClick={() => removeRole(r)}
                        >
                          <DeleteOutlined /> 删除
                        </button>
                        <button type="button" onClick={() => openDetail(r)}>
                          <KeyOutlined /> 详情
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </main>
        ) : null}

        {/* ---------------- 个性配置 ---------------- */}
        {tab === 'theme' ? (
          <main className="sp-main wide">
            <div className="sp-card">
              <div className="sp-card-title"><GlobalOutlined /> 平台名称<small>中文、英文为必填项，其他语种可选</small></div>
              {langs.map((l, i) => (
                <div className="sp-field" key={l.key}>
                  <label className="sp-label">
                    <span className={l.required ? 'req' : ''}>{l.required ? '*' : ''}</span>{l.label}
                  </label>
                  <input
                    className="sp-input"
                    value={l.value}
                    placeholder={l.required ? '请输入平台名称' : '点击选择小语种'}
                    onChange={(e) => {
                      const next = langs.slice();
                      next[i] = { ...l, value: e.target.value };
                      setLangs(next);
                    }}
                  />
                </div>
              ))}
              <div className="sp-actions">
                <button type="button" className="sp-btn primary" onClick={saveTheme}>保存</button>
              </div>
            </div>

            <div className="sp-card">
              <div className="sp-card-title"><PictureOutlined /> 图标配置<small>上传后自动应用至对应位置</small></div>
              <div className="sp-upload-list">
                {H_THEME_ITEMS.map((t) => (
                  <div className="sp-upload-item" key={t.key}>
                    <div className="sp-upload-meta">
                      <div className="sp-upload-label">{t.label}</div>
                      <div className="sp-upload-spec">格式 {t.format} · 建议 {t.size}</div>
                      <div className="sp-upload-usage">{t.usage}</div>
                    </div>
                    <div className="sp-upload-value">{t.value}</div>
                    <button type="button" className="sp-btn ghost" onClick={() => message.info(`上传/替换「${t.label}」`)}>
                      <UploadOutlined /> 重新上传
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </main>
        ) : null}
      </>
        )}
      </div>

      {/* 部门新增 / 重命名 */}
      <Modal
        title={deptModal.key ? '重命名部门' : '新建部门'}
        open={deptModal.open}
        onOk={submitDept}
        onCancel={() => setDeptModal({ open: false, key: null, name: '' })}
        okText="确定"
        cancelText="取消"
        destroyOnHidden
      >
        <Input
          value={deptModal.name}
          onChange={(e) => setDeptModal((s) => ({ ...s, name: e.target.value }))}
          onPressEnter={submitDept}
          placeholder="请输入部门名称"
          maxLength={20}
        />
      </Modal>

      {/* 添加成员（向导：基本信息 → 站点分配） */}
      <Modal
        title="添加成员"
        open={memberModal}
        onCancel={() => setMemberModal(false)}
        footer={null}
        width={560}
        destroyOnHidden
      >
        <Steps
          size="small"
          current={mStep}
          style={{ marginBottom: 22 }}
          items={[{ title: '基本信息' }, { title: '站点分配' }]}
        />
        {mStep === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Input
              value={memberForm.account}
              onChange={(e) => setMemberForm((s) => ({ ...s, account: e.target.value }))}
              placeholder="账号名（登录账号）"
            />
            <Input
              value={memberForm.name}
              onChange={(e) => setMemberForm((s) => ({ ...s, name: e.target.value }))}
              placeholder="姓名 / 工作昵称"
            />
            <Input
              value={memberForm.phone}
              onChange={(e) => setMemberForm((s) => ({ ...s, phone: e.target.value }))}
              placeholder="手机号（可选）"
            />
            <div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', marginBottom: 6 }}>部门</div>
              <Select
                style={{ width: '100%' }}
                value={memberForm.dept}
                onChange={(v) => setMemberForm((s) => ({ ...s, dept: v }))}
                options={deptOptions.map((v) => ({ value: v, label: v }))}
              />
            </div>
            <div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', marginBottom: 6 }}>角色</div>
              <Select
                style={{ width: '100%' }}
                value={memberForm.role}
                onChange={(v) => setMemberForm((s) => ({ ...s, role: v }))}
                options={roleOptions.map((v) => ({ value: v, label: v }))}
              />
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', marginBottom: 8 }}>站点分配方式（单选，默认省市区）</div>
              <div className="sp-options">
                {H_ASSIGN_MODES.map((m) => (
                  <label key={m} className={`sp-option${assign.mode === m ? ' active' : ''}`}>
                    <input type="radio" checked={assign.mode === m} onChange={() => setAssign((s) => ({ ...s, mode: m }))} />
                    {m}
                  </label>
                ))}
              </div>
            </div>
            {assign.mode === '省市区' && (
              <div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', marginBottom: 6 }}>选择国家 / 地区（可多选）</div>
                <Select
                  mode="multiple"
                  style={{ width: '100%' }}
                  placeholder="选择地区，将该地区的站点分配给成员"
                  value={assign.regions}
                  onChange={(v) => setAssign((s) => ({ ...s, regions: v }))}
                  options={PORTAL_REGIONS.map((r) => ({ value: r, label: r }))}
                />
              </div>
            )}
            {assign.mode === '站点标签' && (
              <div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', marginBottom: 6 }}>选择站点标签（可多选）</div>
                <Select
                  mode="multiple"
                  style={{ width: '100%' }}
                  placeholder="选择具有某些标签的站点"
                  value={assign.tags}
                  onChange={(v) => setAssign((s) => ({ ...s, tags: v }))}
                  options={STATION_TAGS.map((t) => ({ value: t.name, label: `${t.name}（${t.count}）` }))}
                />
              </div>
            )}
            {assign.mode === '外部公司' && (
              <div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', marginBottom: 6 }}>选择外部公司</div>
                <Select
                  style={{ width: '100%' }}
                  placeholder="该公司分享给本公司的全部站点将分配给成员"
                  value={assign.company}
                  onChange={(v) => setAssign((s) => ({ ...s, company: v }))}
                  options={H_COMPANIES.map((c) => ({ value: c.name, label: c.name }))}
                />
              </div>
            )}
            {assign.mode === '手动选择' && (
              <div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', marginBottom: 6 }}>手动选择站点（可多选）</div>
                <Select
                  mode="multiple"
                  style={{ width: '100%' }}
                  placeholder="选择一个或多个站点分配给成员"
                  value={assign.stations}
                  onChange={(v) => setAssign((s) => ({ ...s, stations: v }))}
                  options={STATIONS.map((s) => ({ value: s.name, label: s.name }))}
                />
              </div>
            )}
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', lineHeight: 1.9 }}>
              站点分配决定成员在能源管家内的数据权限；点击「生成邀请链接」后可在「待激活的邀请」中查看（后续修改需进入能源管家后台）。
            </div>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 }}>
          {mStep === 0 ? (
            <button type="button" className="sp-btn ghost" onClick={() => setMemberModal(false)}>取消</button>
          ) : (
            <button type="button" className="sp-btn ghost" onClick={() => setMStep(0)}>上一步</button>
          )}
          {mStep === 0 ? (
            <button type="button" className="sp-btn primary" onClick={goMemberNext}>下一步</button>
          ) : (
            <button type="button" className="sp-btn primary" onClick={submitMember}>生成邀请链接</button>
          )}
        </div>
      </Modal>

      {/* 编辑成员（部门 / 角色） */}
      <Modal
        title={`编辑成员 · ${editMember.name}`}
        open={editMember.open}
        onOk={submitEditMember}
        onCancel={() => setEditMember((s) => ({ ...s, open: false }))}
        okText="确定"
        cancelText="取消"
        destroyOnHidden
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', marginBottom: 6 }}>部门</div>
            <Select
              style={{ width: '100%' }}
              value={editMember.dept}
              onChange={(v) => setEditMember((s) => ({ ...s, dept: v }))}
              options={assignDepts.map((v) => ({ value: v, label: v }))}
            />
          </div>
          <div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', marginBottom: 6 }}>角色</div>
            <Select
              style={{ width: '100%' }}
              value={editMember.role}
              onChange={(v) => setEditMember((s) => ({ ...s, role: v }))}
              options={(assignRoles.includes(editMember.role) ? assignRoles : [editMember.role, ...assignRoles])
                .map((v) => ({ value: v, label: v }))}
            />
          </div>
        </div>
      </Modal>

      {/* 角色权限详情 */}
      <Modal
        title={`角色权限详情 · ${detailPerm.role}`}
        open={detailPerm.open}
        onCancel={() => setDetailPerm((s) => ({ ...s, open: false }))}
        footer={null}
        width={520}
        destroyOnHidden
      >
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', marginBottom: 8 }}>可访问产品</div>
          <div className="sp-options">
            {detailPerm.products.length
              ? detailPerm.products.map((p) => <span key={p} className="sp-tag mini">{p}</span>)
              : <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13 }}>未配置产品</span>}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', marginBottom: 8 }}>可见功能菜单（{detailPerm.menus.length} 项）</div>
          {detailPerm.menus.length ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {detailPerm.menus.map((k) => (
                <span key={k} className="sp-tag mini">{LABEL_BY_KEY[k] || k}</span>
              ))}
            </div>
          ) : (
            <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13 }}>暂未配置菜单权限，请点击「权限配置」勾选。</div>
          )}
        </div>
      </Modal>

      {/* 新增 / 复制角色 */}
      <Modal
        title={roleModal.copyFrom ? '复制角色' : '新增角色'}
        open={roleModal.open}
        onOk={submitRole}
        onCancel={() => setRoleModal({ open: false, name: '', copyFrom: null })}
        okText="确定"
        cancelText="取消"
        destroyOnHidden
      >
        <Input
          value={roleModal.name}
          onChange={(e) => setRoleModal((s) => ({ ...s, name: e.target.value }))}
          onPressEnter={submitRole}
          placeholder="请输入角色名称（不可与已有角色重复）"
          maxLength={20}
        />
        <div style={{ marginTop: 10, fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>
          {roleModal.copyFrom
            ? '将原角色的权限配置复制为新角色，需填写新的角色名称（不可与已有角色名称重复）。'
            : '新增后需点击角色行的「权限配置」勾选产品与功能菜单。'}
        </div>
      </Modal>
    </div>
  );
};

export default SettingsPage;